import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FileText, Download, Image as ImageIcon, ZoomIn, ZoomOut, Loader2, X, CheckCircle2, XCircle, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Document, Page, pdfjs } from "react-pdf";
import { Progress } from "@/components/ui/progress";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const AVATAR_BASE = process.env.REACT_APP_BACKEND_URL;

export const UserAvatar = ({ userId, avatarId, name, size = 32, className = "" }) => {
  const [err, setErr] = useState(false);
  const initials = (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  if (userId && avatarId && !err) {
    return (
      <img
        src={`${AVATAR_BASE}/api/avatars/${userId}?v=${avatarId}`}
        onError={() => setErr(true)}
        alt={name || ""}
        style={{ width: size, height: size }}
        className={`rounded-full object-cover shrink-0 ${className}`}
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size, fontSize: Math.max(10, size * 0.36) }}
      className={`rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold shrink-0 ${className}`}
    >
      {initials}
    </div>
  );
};

export const deadlineLabel = (r) => formatDate(r.deadline) + (r.deadline_time ? ` · ${r.deadline_time}` : "");

export const dropProps = (onFiles, setDragOver) => ({
  onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); },
  onDragLeave: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); },
  onDrop: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); if (e.dataTransfer?.files?.length) onFiles(e.dataTransfer.files); },
});

export const UploadProgress = ({ progress }) => {
  if (!progress) return null;
  return (
    <div className="space-y-1.5 w-full" data-testid="upload-progress-bar">
      <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
        <span className="truncate">{progress.label || "Dosya yükleniyor..."}</span>
        <span className="font-semibold text-slate-900 shrink-0" data-testid="upload-progress-percent">%{progress.percent ?? 0}</span>
      </div>
      <Progress value={progress.percent ?? 0} className="h-1.5 rounded-none bg-slate-200" />
    </div>
  );
};

export const STATUS_META = {
  beklemede: { label: "Beklemede", cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  teklif_verildi: { label: "Teklif verildi", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  suresi_doldu: { label: "Süresi doldu", cls: "bg-red-500/10 text-red-500 border-red-500/20" },
  pending: { label: "Değerlendirmede", cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  accepted: { label: "Kabul edildi", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  rejected: { label: "Reddedildi", cls: "bg-red-500/10 text-red-500 border-red-500/20" },
};

export const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, cls: "" };
  return (
    <Badge variant="outline" className={`rounded-none text-xs font-medium ${m.cls}`} data-testid={`status-badge-${status}`}>
      {m.label}
    </Badge>
  );
};

export const typeLabel = (t) => (t === "custom" ? "Custom Stand" : t === "sablon" ? "Şablon Stand" : t || "-");

export const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export const projectInfo = (p) => {
  if (!p) return "";
  if (p.zone) return `${p.zone} · Stand ${p.stand_no} · ${p.project_type}${p.area_m2 ? ` · ${p.area_m2} m²` : ""}`;
  const parts = [p.category || "Müşteri Projesi"];
  if (p.location) parts.push(p.location);
  if (p.event_date) parts.push(formatDate(p.event_date));
  return parts.join(" · ");
};

export const SearchClear = ({ value, onClear, testid = "search-clear" }) =>
  value ? (
    <button
      type="button"
      onClick={onClear}
      data-testid={testid}
      aria-label="Aramayı temizle"
      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors duration-150"
    >
      <X size={14} />
    </button>
  ) : null;

export const ZONE_STATUSES = {
  "Green Zone": ["Özel", "SBB"],
  "Blue Zone": ["Custom", "Customised Template", "Template"],
};
export const ALL_STATUSES = ["Özel", "SBB", "Custom", "Customised Template", "Template"];
export const statusesForZone = (zone) => ZONE_STATUSES[zone] || ALL_STATUSES;
export const statusColorCls = (st) =>
  st === "SBB" ? "border-blue-300 text-blue-700 bg-blue-50"
  : st === "Özel" ? "border-emerald-300 text-emerald-700 bg-emerald-50"
  : st === "Custom" ? "border-cyan-300 text-cyan-700 bg-cyan-50"
  : st === "Customised Template" ? "border-indigo-300 text-indigo-700 bg-indigo-50"
  : st === "Template" ? "border-slate-400 text-slate-700 bg-slate-100"
  : "border-amber-300 text-amber-700 bg-amber-50";

export const hubsForZone = (hubs, zone) =>
  (hubs || []).filter((h) => !zone || h.zone === "all" || h.zone === zone).map((h) => h.name);

export const trFold = (s) =>
  String(s || "")
    .toLocaleLowerCase("tr")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i");

export const daysLeft = (deadline) => {
  const diff = Math.ceil((new Date(deadline + "T23:59:59") - new Date()) / 86400000);
  return diff;
};

export const ServiceLists = ({ included = [], excluded = [] }) => {
  if (!included?.length && !excluded?.length) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="service-lists">
      {included?.length > 0 && (
        <div className="border border-emerald-200 bg-emerald-50/40 p-4" data-testid="included-services">
          <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-2">Dahil Hizmetler</div>
          <ul className="space-y-1.5">
            {included.map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm"><CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" /> {t}</li>
            ))}
          </ul>
        </div>
      )}
      {excluded?.length > 0 && (
        <div className="border border-red-200 bg-red-50/40 p-4" data-testid="excluded-services">
          <div className="text-xs uppercase tracking-wider text-red-600 font-semibold mb-2">Hariç Hizmetler</div>
          <ul className="space-y-1.5">
            {excluded.map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm"><XCircle size={14} className="text-red-500 mt-0.5 shrink-0" /> {t}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const parsePrice = (raw) => {
  let s = String(raw ?? "").trim().replace(/\s/g, "").replace(/(tl|try|₺|eur|€|usd|\$)/gi, "");
  if (!s) return NaN;
  const hasDot = s.includes(".");
  const hasComma = s.includes(",");
  if (hasDot && hasComma) {
    if (s.lastIndexOf(",") > s.lastIndexOf(".")) s = s.replace(/\./g, "").replace(",", ".");
    else s = s.replace(/,/g, "");
  } else if (hasComma) {
    const parts = s.split(",");
    if (parts.length === 2 && parts[1].length !== 3) s = s.replace(",", ".");
    else s = s.replace(/,/g, "");
  } else if (hasDot) {
    const parts = s.split(".");
    if (!(parts.length === 2 && parts[1].length !== 3)) s = s.replace(/\./g, "");
  }
  return parseFloat(s);
};

export const deadlineExpired = (deadline, time) =>
  new Date() > new Date(`${deadline}T${time || "23:59"}:59`);

export const DeadlineWarning = ({ deadline, time }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const end = new Date(`${deadline}T${time || "23:59"}:59`).getTime();
  if (now > end)
    return <span className="text-xs text-red-500 font-semibold" data-testid="deadline-ended">İhale Sona Erdi</span>;
  const d = daysLeft(deadline);
  if (d <= 1) {
    const mins = Math.floor((end - now) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return (
      <span className="text-xs text-red-500 font-semibold" data-testid="deadline-last-day">
        Son Gün! · {h > 0 ? `${h} sa ${m} dk` : `${m} dk`} kaldı
      </span>
    );
  }
  if (d <= 3) return <span className="text-xs text-red-500">Son {d} gün!</span>;
  return <span className="text-xs text-slate-500">{d} gün kaldı</span>;
};

const previewKind = (f) => {
  const ct = f.content_type || "";
  if (ct === "application/pdf") return "pdf";
  if (ct.startsWith("image/")) return "image";
  if (ct.startsWith("text/")) return "text";
  if (["officedocument", "ms-excel", "msword", "ms-powerpoint"].some((x) => ct.includes(x)) || /\.(pptx?|docx?|xlsx?)$/i.test(f.original_filename || "")) return "office";
  return "none";
};

export const SALE_STATUSES = ["Teklif Hazırlanıyor", "Teklif Gönderildi", "Dönüş Bekleniyor", "Teklif Onaylandı", "Sözleşme Gönderildi", "Sözleşme İmzalandı", "Ödeme Alındı", "Tema Kurulmadı"];

let _saleStatuses = [...SALE_STATUSES];
let _saleFetched = false;
const _saleListeners = new Set();
const _notifySale = () => _saleListeners.forEach((fn) => fn([..._saleStatuses]));

export const useSaleStatuses = () => {
  const [list, setList] = useState([..._saleStatuses]);
  useEffect(() => {
    _saleListeners.add(setList);
    if (!_saleFetched) {
      _saleFetched = true;
      api.get("/settings/sale-statuses").then((r) => { _saleStatuses = r.data.statuses; _notifySale(); }).catch(() => { _saleFetched = false; });
    } else {
      setList([..._saleStatuses]);
    }
    return () => _saleListeners.delete(setList);
  }, []);
  return list;
};

export const setSaleStatusesCache = (statuses) => { _saleStatuses = [...statuses]; _notifySale(); };

export const SALE_STATUS_HEX = {
  "Teklif Hazırlanıyor": "#94a3b8",
  "Teklif Gönderildi": "#3b82f6",
  "Dönüş Bekleniyor": "#f97316",
  "Teklif Onaylandı": "#8b5cf6",
  "Sözleşme Gönderildi": "#f59e0b",
  "Sözleşme İmzalandı": "#14b8a6",
  "Ödeme Alındı": "#059669",
  "Tema Kurulmadı": "#e11d48",
};

const SALE_STATUS_CLS = {
  "Teklif Hazırlanıyor": "border-slate-300 text-slate-600 bg-slate-50",
  "Teklif Gönderildi": "border-blue-300 text-blue-700 bg-blue-50",
  "Dönüş Bekleniyor": "border-orange-300 text-orange-700 bg-orange-50",
  "Teklif Onaylandı": "border-violet-300 text-violet-700 bg-violet-50",
  "Sözleşme Gönderildi": "border-amber-300 text-amber-700 bg-amber-50",
  "Sözleşme İmzalandı": "border-teal-300 text-teal-700 bg-teal-50",
  "Ödeme Alındı": "border-emerald-300 text-emerald-700 bg-emerald-50",
  "Tema Kurulmadı": "border-rose-300 text-rose-700 bg-rose-50",
};

export const saleStatusCls = (v) => SALE_STATUS_CLS[v] || "border-slate-200 text-slate-500 bg-white";

const PROD_STATUS_CLS = {
  "Üretim Başlamadı": "border-slate-300 text-slate-600 bg-slate-50",
  "Üretim Başladı": "border-indigo-300 text-indigo-700 bg-indigo-50",
  "Paletlendi": "border-cyan-300 text-cyan-700 bg-cyan-50",
  "Kurulum Başladı": "border-blue-300 text-blue-700 bg-blue-50",
  "Boya Başladı": "border-amber-300 text-amber-700 bg-amber-50",
  "Görseller Takılıyor": "border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50",
  "Extra Atılıyor": "border-orange-300 text-orange-700 bg-orange-50",
  "Çizim Bekliyor": "border-violet-300 text-violet-700 bg-violet-50",
  "Teslim edildi": "border-emerald-300 text-emerald-700 bg-emerald-50",
  "Teslim Edildi": "border-emerald-300 text-emerald-700 bg-emerald-50",
};
const PROD_STATUS_PALETTE = [
  "border-teal-300 text-teal-700 bg-teal-50",
  "border-rose-300 text-rose-700 bg-rose-50",
  "border-lime-300 text-lime-700 bg-lime-50",
  "border-sky-300 text-sky-700 bg-sky-50",
  "border-purple-300 text-purple-700 bg-purple-50",
  "border-yellow-300 text-yellow-700 bg-yellow-50",
];
export const prodStatusCls = (v) => {
  if (!v) return "bg-white text-slate-400 border-slate-200";
  if (PROD_STATUS_CLS[v]) return PROD_STATUS_CLS[v];
  let h = 0;
  for (let i = 0; i < v.length; i++) h = (h * 31 + v.charCodeAt(i)) >>> 0;
  return PROD_STATUS_PALETTE[h % PROD_STATUS_PALETTE.length];
};

export const maskAmount = (s) => {
  let v = String(s).replace(/[^\d,]/g, "");
  const i = v.indexOf(",");
  if (i !== -1) v = v.slice(0, i + 1) + v.slice(i + 1).replace(/,/g, "").slice(0, 2);
  const [int, dec] = v.split(",");
  const fmtInt = (int || "").replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return dec !== undefined ? `${fmtInt},${dec}` : fmtInt;
};

export const parseAmount = (s) => {
  const n = parseFloat(String(s).replace(/\./g, "").replace(",", "."));
  return isNaN(n) ? null : n;
};

export const fmtAmountTr = (n) => new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export const CUR_LABEL = { TRY: "TL", USD: "Dolar", EUR: "Euro" };

export const OfferAmountCell = ({ p, onSave, locked = false, amountKey = "offer_amount", currencyKey = "offer_currency", idp = "offer" }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const amount = p[amountKey];
  const currency = p[currencyKey];
  const commit = () => {
    setEditing(false);
    const n = parseAmount(val);
    if (n !== (amount ?? null)) onSave(p, { [amountKey]: n ?? -1 });
  };
  if (locked) {
    return (
      <div className="flex items-center justify-end gap-1 mt-1.5 text-xs" data-testid={`${idp}-amount-wrap-${p.id}`}>
        <span className="font-semibold text-slate-700 tabular-nums text-right min-w-[84px] inline-block" data-testid={`${idp}-amount-cell-${p.id}`}>
          {amount != null ? `${fmtAmountTr(amount)} ${CUR_LABEL[currency || "TRY"]}` : "—"}
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5" data-testid={`${idp}-amount-wrap-${p.id}`}>
      {editing ? (
        <Input
          autoFocus
          value={val}
          inputMode="decimal"
          onChange={(e) => setVal(maskAmount(e.target.value))}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") setEditing(false);
          }}
          placeholder="örn. 1.250.000,50"
          data-testid={`${idp}-amount-input-${p.id}`}
          className="rounded-none h-7 w-[120px] text-xs bg-white border-slate-300 text-right"
        />
      ) : (
        <button
          type="button"
          onClick={() => { setVal(amount != null ? fmtAmountTr(amount) : ""); setEditing(true); }}
          data-testid={`${idp}-amount-cell-${p.id}`}
          title="Tutarı düzenle"
          className="group inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 transition-colors duration-150"
        >
          <span className={`inline-block min-w-[84px] text-right tabular-nums ${amount != null ? "font-semibold" : "text-slate-400"}`}>
            {amount != null ? fmtAmountTr(amount) : "Tutar ekle"}
          </span>
          <Pencil size={10} className="text-slate-300 group-hover:text-slate-500 transition-colors duration-150" />
        </button>
      )}
      <Select value={currency || "TRY"} onValueChange={(v) => onSave(p, { [currencyKey]: v })}>
        <SelectTrigger data-testid={`${idp}-currency-select-${p.id}`} className="rounded-none h-7 w-[72px] text-[11px] font-semibold border-slate-200 bg-white px-2">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-white border-slate-200 rounded-none">
          {Object.entries(CUR_LABEL).map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
};

export const FileChips = ({ files, onRemove, grid = false }) => {
  const [preview, setPreview] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [numPages, setNumPages] = useState(0);
  const [loadingId, setLoadingId] = useState(null);

  if (!files || files.length === 0) return <span className="text-xs text-slate-500">Dosya yok</span>;

  const handleDownload = async (f) => {
    try {
      await downloadFile(f);
    } catch {
      toast.error("Dosya indirilemedi");
    }
  };

  const openPreview = async (f) => {
    setLoadingId(f.id);
    try {
      const kind = previewKind(f);
      if (kind === "office") {
        const { data } = await api.get(`/files/${f.id}/preview-url`);
        setZoom(100);
        setNumPages(0);
        setPreview({ file: f, url: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(data.url)}`, kind: "office", noRevoke: true });
        return;
      }
      const res = await api.get(`/files/${f.id}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: f.content_type || "application/octet-stream" });
      setZoom(100);
      setNumPages(0);
      setPreview({ file: f, url: URL.createObjectURL(blob), kind });
    } catch {
      toast.error("Dosya açılamadı");
    } finally {
      setLoadingId(null);
    }
  };

  const closePreview = () => {
    if (preview && !preview.noRevoke) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const zoomable = preview && (preview.kind === "pdf" || preview.kind === "image");

  return (
    <>
      <div className={grid ? "grid grid-cols-2 sm:grid-cols-3 gap-2" : "flex flex-wrap gap-2"}>
        {files.map((f) => (
          <Button
            key={f.id}
            variant="outline"
            size="sm"
            className={`rounded-none h-8 text-xs gap-2 border-slate-200 hover:bg-slate-100 ${grid ? "w-full justify-start min-w-0" : ""}`}
            onClick={() => openPreview(f)}
            data-testid={`file-preview-${f.id}`}
            title="Önizle"
          >
            {loadingId === f.id ? <Loader2 size={13} className="animate-spin" /> : f.content_type?.startsWith("image/") ? <ImageIcon size={13} /> : <FileText size={13} />}
            <span className={grid ? "flex-1 min-w-0 truncate text-left" : "max-w-[160px] truncate"}>{f.original_filename}</span>
            <span
              role="button"
              tabIndex={0}
              className="text-slate-500 hover:text-slate-900 transition-colors duration-200"
              onClick={(e) => { e.stopPropagation(); handleDownload(f); }}
              data-testid={`file-download-${f.id}`}
              title="İndir"
            >
              <Download size={12} />
            </span>
            {onRemove && (
              <span
                role="button"
                tabIndex={0}
                className="text-slate-500 hover:text-red-600 transition-colors duration-200"
                onClick={(e) => { e.stopPropagation(); onRemove(f.id); }}
                data-testid={`file-remove-${f.id}`}
                title="Kaldır"
              >
                <X size={12} />
              </span>
            )}
          </Button>
        ))}
      </div>
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-5xl w-[92vw] h-[88vh] flex flex-col p-0 gap-0" data-testid="file-preview-modal">
          {preview && (
            <>
              <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 pr-12 shrink-0">
                <DialogTitle className="text-sm font-medium truncate" data-testid="preview-filename">{preview.file.original_filename}</DialogTitle>
                <div className="flex items-center gap-2 shrink-0">
                  {zoomable && (
                    <>
                      <Button variant="outline" size="sm" className="rounded-none h-8 border-slate-200 hover:bg-slate-100" onClick={() => setZoom((z) => Math.max(25, z - 25))} disabled={zoom <= 25} data-testid="preview-zoom-out" title="Uzaklaştır">
                        <ZoomOut size={14} />
                      </Button>
                      <span className="text-xs text-slate-500 w-12 text-center" data-testid="preview-zoom-level">%{zoom}</span>
                      <Button variant="outline" size="sm" className="rounded-none h-8 border-slate-200 hover:bg-slate-100" onClick={() => setZoom((z) => Math.min(400, z + 25))} disabled={zoom >= 400} data-testid="preview-zoom-in" title="Yakınlaştır">
                        <ZoomIn size={14} />
                      </Button>
                    </>
                  )}
                  <Button size="sm" className="rounded-none h-8 bg-slate-900 text-white hover:bg-slate-700 gap-1.5 font-semibold transition-colors duration-200" onClick={() => handleDownload(preview.file)} data-testid="preview-download-button">
                    <Download size={13} /> İndir
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-auto bg-slate-100">
                {preview.kind === "pdf" && (
                  <div className="min-h-full flex flex-col items-center gap-4 p-4" data-testid="preview-pdf-frame">
                    <Document
                      file={preview.url}
                      onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                      loading={<p className="text-slate-500 text-sm py-10">PDF yükleniyor...</p>}
                      error={<p className="text-red-500 text-sm py-10">PDF görüntülenemedi. Lütfen dosyayı indirin.</p>}
                    >
                      {Array.from({ length: numPages }, (_, i) => (
                        <Page key={i} pageNumber={i + 1} width={Math.round(Math.min(800, window.innerWidth - 48) * (zoom / 100))} renderTextLayer={false} renderAnnotationLayer={false} className="mb-4 shadow-lg" />
                      ))}
                    </Document>
                  </div>
                )}
                {preview.kind === "image" && (
                  <div className="min-h-full flex items-start justify-center p-4">
                    <img src={preview.url} alt={preview.file.original_filename} style={{ width: `${zoom}%`, maxWidth: "none" }} data-testid="preview-image" />
                  </div>
                )}
                {preview.kind === "office" && (
                  <iframe title="office-preview" src={preview.url} className="w-full h-full bg-white" data-testid="preview-office-frame" allowFullScreen />
                )}
                {preview.kind === "text" && (
                  <iframe title="text-preview" src={preview.url} className="w-full h-full bg-white" data-testid="preview-text-frame" />
                )}
                {preview.kind === "none" && (
                  <div className="h-full flex flex-col items-center justify-center gap-4 text-slate-500" data-testid="preview-unsupported">
                    <FileText size={40} />
                    <p className="text-sm">Bu dosya türü için önizleme desteklenmiyor.</p>
                    <Button className="rounded-none bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200" onClick={() => handleDownload(preview.file)}>
                      <Download size={14} /> Dosyayı İndir
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const areaNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

export const KpiCard = ({ icon: Icon, label, value, accent = "bg-slate-100 text-slate-600", testid, onClick, active = false }) => {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`bg-white border px-4 py-3.5 flex items-center gap-3 text-left w-full ${active ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200"} ${onClick ? "cursor-pointer hover:border-slate-500 transition-colors duration-150" : ""}`}
      data-testid={testid}
    >
      <div className={`w-9 h-9 flex items-center justify-center shrink-0 ${accent}`}><Icon size={16} /></div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold truncate">{label}</div>
        <div className="font-heading text-2xl font-extrabold tracking-tight tabular-nums leading-none mt-1">{value}</div>
      </div>
    </Comp>
  );
};

export const BASKI_STAGES = ["Üretim Başladı", "Üretim Tamamlandı", "Alana Sevk Edildi", "Montaj Yapıldı", "İcmal dosyası çıktı", "Faturası kesildi"];
export const BASKI_STAGE_COLORS = {
  "Üretim Başladı": { badge: "bg-amber-100 text-amber-800 border-amber-300", bar: "bg-amber-400", dot: "bg-amber-400" },
  "Üretim Tamamlandı": { badge: "bg-sky-100 text-sky-800 border-sky-300", bar: "bg-sky-400", dot: "bg-sky-400" },
  "Alana Sevk Edildi": { badge: "bg-violet-100 text-violet-800 border-violet-300", bar: "bg-violet-400", dot: "bg-violet-400" },
  "Montaj Yapıldı": { badge: "bg-rose-100 text-rose-800 border-rose-300", bar: "bg-rose-400", dot: "bg-rose-400" },
  "İcmal dosyası çıktı": { badge: "bg-teal-100 text-teal-800 border-teal-300", bar: "bg-teal-400", dot: "bg-teal-400" },
  "Faturası kesildi": { badge: "bg-emerald-100 text-emerald-800 border-emerald-300", bar: "bg-emerald-400", dot: "bg-emerald-400" },
};
export const stageBadgeCls = (s) => BASKI_STAGE_COLORS[s]?.badge || "bg-slate-100 text-slate-500 border-slate-200";
